import React from 'react';
import { useNoteContext } from './context/NoteContext';
import { formatSavedDate } from '@/utils/formatDate';

const SpinnerIcon: React.FC = () => (
  <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const ErrorIcon: React.FC = () => (
  <svg className="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SavingStatusIndicator: React.FC = () => {
  const { savingStatus } = useNoteContext();
  const { state, contentUpdatedAt, userName, viewingAt } = savingStatus;

  type Config = { text: string; icon: React.ReactNode; colorClass: string };

  const getConfig = (): Config => {
    switch (state) {
      case 'idle':
        return {
          text: contentUpdatedAt
            ? `Last change by ${userName ?? 'Unknown'} · ${formatSavedDate(contentUpdatedAt)}`
            : 'Saved',
          icon: null,
          colorClass: 'text-gray-400 dark:text-gray-500',
        };
      case 'editing':
        return {
          text: 'Editing',
          icon: null,
          colorClass: 'text-gray-500 dark:text-gray-400',
        };
      case 'saving':
        return {
          text: 'Saving…',
          icon: <SpinnerIcon />,
          colorClass: 'text-gray-400 dark:text-gray-500',
        };
      case 'viewing':
        return {
          text: viewingAt
            ? `Viewing version · ${formatSavedDate(viewingAt)}`
            : 'Viewing version',
          icon: null,
          colorClass: 'text-amber-600 dark:text-amber-400',
        };
      case 'error':
        return {
          text: 'Error',
          icon: <ErrorIcon />,
          colorClass: 'text-red-600 dark:text-red-500',
        };
    }
  };

  const { text, icon, colorClass } = getConfig();

  return (
    <div className={`hidden sm:flex items-center gap-1.5 text-sm px-2 transition-colors duration-200 ${colorClass}`}>
      {icon}
      <span>{text}</span>
    </div>
  );
};

export default SavingStatusIndicator;
